<script setup>
import { computed } from 'vue'
import ShowOptions from '@components/show/show-options'
import formatTime from '@utils/format-time'
import formatTimeAMPM from '@utils/format-time-am-pm'
import formatDate from '@utils/format-date'

const props = defineProps({
    artwork: {
        type: Boolean,
        default: false,
    },
    scheduleItem: {
        type: Object,
        default: () => {
            return {
                show: {
                    id: 'id',
                    title: 'showTitle',
                    url: 'showUrl',
                    type: 'show',
                    image: {},
                    genre: [],
                    artist_id: [],
                    artist: [],
                },
            }
        },
    },
})

const show = computed(() => props.scheduleItem.show)

const date = computed(() => new Date(props.scheduleItem.air_start))

const airTime = computed(() =>
    props.artwork ? formatTimeAMPM(date.value) : formatTime(date.value)
)

const airDate = computed(() => formatDate(date.value))
</script>

<template>
    <div
        v-if="show"
        :class="['show-schedule-tile', artwork && 'show-schedule-tile-artwork']"
    >
        <div class="date-time">
            <BasePlaceholder
                class="time"
                :text="airTime"
                tag="div"
            />
            <BasePlaceholder
                v-if="artwork"
                class="date"
                :text="airDate"
                tag="div"
            />
        </div>

        <BaseResourceImage
            v-if="artwork"
            :resource="show"
            class="image"
            radius="list"
        />
        <div class="show">
            <BaseLink
                v-if="show.artist && show.artist.title"
                :class="['title', 'show-link']"
                :to="{ path: `/artist/${show.artist.url}/${show.artist.id}` }"
            >
                {{ show.artist.title }}
            </BaseLink>
            <BasePlaceholder
                v-else
                :class="['title', 'show-link']"
                :text="show.artist && show.artist.title"
                type="artist"
                tag="div"
            />

            <div
                v-if="show && show.title"
                :class="['subtitle', 'artist']"
            >
                <BaseLink :to="{ name: 'show', params: { id: show.id } }">
                    {{ show.title }}
                </BaseLink>
            </div>

            <BasePlaceholder
                v-else
                :text="show && show.title"
                :class="['subtitle', 'artist']"
                tag="div"
                type="show"
            />
        </div>
        <div @click.prevent="() => {}">
            <BaseDropdown :padded="false">
                <template #trigger>
                    <BaseButton
                        :modifiers="{ autoWidth: true, transparent: true, dots: true }"
                        button-type="tag"
                        aria-label="Options"
                        size="small"
                    />
                </template>
                <template #dropdown>
                    <div>
                        <ShowOptions :show="show" />
                    </div>
                </template>
            </BaseDropdown>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.show-schedule-tile {
    position: relative;
    display: flex;
    align-items: center;
    padding-top: $s-s;
    padding-bottom: $s-s;
    margin: 0;
    border-bottom: 1px solid $c-surface-raised;
}

.date-time {
    display: inline-block;
    width: 40px;
    min-height: 38px;

    .show-schedule-tile-artwork & {
        width: auto;

        @include desktop {
            width: span(1 of 12 narrow);
        }
    }
}

.time {
    display: inline-block;
    width: 100%;
    margin: 0 0 $s-xxs;
    font-size: 13px;
    font-weight: bold;
    line-height: 16px;
    vertical-align: top;
    letter-spacing: 0.5px;
    white-space: nowrap;
}

.show-schedule-tile:nth-of-type(1) {
    .time {
        color: $c-text;
    }
}

.show {
    display: inline-block;
    width: 70%;
    padding-left: $s-l;
    margin-right: auto;
    vertical-align: top;

    .show-schedule-tile-artwork & {
        @include desktop {
            display: flex;
        }
    }

    .show-schedule-tile-artwork &-link {
        @include desktop {
            min-width: 55%;
        }
    }
}

.title {
    @extend %contentTitleM;

    max-width: 220px;
    margin: 0 0 $s-xxs;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-decoration: none;
}

.artist {
    @extend %contentSubtitleM;

    max-width: 100%;
    height: 18px;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
    white-space: nowrap;

    a {
        color: $c-text-secondary;
    }
}

@media (min-width: $desktop) {
    .time {
        color: $c-text;
    }
}

.image {
    display: none;
    width: 2rem;
    margin-left: gutter(of 12 narrow);

    @include tablet {
        display: block;
    }
}

.date {
    display: none;

    @extend %contentSubtitleS;

    @include desktop {
        display: block;
        white-space: nowrap;
    }
}
</style>
